import { useEffect, useRef } from 'react';
import type { QueryObserverResult } from '@tanstack/react-query';

import '@/shared/features/quiz-folder/consts/events.quiz-folder';

import { trackEvent } from '@zep/logger/tracking';

import { useIsLoggerUserInfoSet } from '@/lib/auth';
import type { Folder } from '@/shared/features/quiz-folder';

/**
 * 폴더 상세를 "보는 사람 기준"으로 맞추고 `page_view` 를 찍는다. 폴더·보는 사람 조합마다 한 번.
 *
 * - SSR 은 인증 헤더 없이 상세를 받으므로 첫 응답의 `playAction` 은 익명 기준이다. 로그인 사용자는 여기서
 *   한 번 다시 받고, **그 응답으로** 찍는다 — 화면의 CTA 와 이벤트가 같은 값을 봐야 한다. 재조회가 실패하면
 *   익명 값을 소유자 값인 양 찍지 않는다.
 * - 게스트는 SSR 응답이 곧 자기 기준이라 바로 찍는다.
 * - 유저 정보가 로거에 실린 뒤에 찍는다 — 먼저 나가면 같은 이벤트가 유저 컨텍스트 유무로 섞인다.
 */
export const useFolderDetailPageView = ({
  folder,
  isOwner,
  viewerId,
  refetch,
}: {
  folder: Folder | undefined;
  isOwner: boolean;
  viewerId: string | undefined;
  refetch: () => Promise<QueryObserverResult<Folder>>;
}) => {
  const isLoggerUserInfoSet = useIsLoggerUserInfoSet();
  const viewedKeyRef = useRef<string>();
  // 재조회 응답이 도착했을 때의 최신 판정. effect 의존성에 넣으면 응답마다 다시 돌아 `active` 가 꺼진다.
  const latestRef = useRef({ folder, isOwner });
  latestRef.current = { folder, isOwner };

  const folderId = folder?.id;

  useEffect(() => {
    if (!folderId || !isLoggerUserInfoSet) return;
    const viewKey = `${folderId}:${viewerId ?? 'guest'}`;
    if (viewedKeyRef.current === viewKey) return;
    viewedKeyRef.current = viewKey;

    const track = (viewed: Folder) =>
      trackEvent('quiz_folder_detail_page_view', {
        folder_id: viewed.id,
        visibility: viewed.visibility,
        quiz_count: viewed.quizsetCount,
        is_folder_owner: latestRef.current.isOwner,
        play_action: viewed.playAction,
      });

    if (!viewerId) {
      const { folder: current } = latestRef.current;
      if (current) track(current);
      return;
    }

    let active = true;
    void refetch().then(result => {
      if (active && result.isSuccess && result.data?.id === folderId) {
        track(result.data);
      }
    });
    return () => {
      active = false;
    };
  }, [folderId, viewerId, isLoggerUserInfoSet, refetch]);
};
